import React, { useEffect, useState } from "react";
import { FiBell, FiKey, FiFileText, FiList } from "react-icons/fi";
import { AiFillEye, AiFillEyeInvisible, AiOutlineSwap } from "react-icons/ai";
import { MdArrowForwardIos } from "react-icons/md";
import { useForm } from "react-hook-form";
import { siteRoutes } from "utils/helpers/enums/routes";
import AttendeeBreadcrumb, {
  AttendeBreadcrumbLink,
} from "components/breadcrumbs/AttendeeBreadcrumb";
import useStore from "hooks/useStore";
import { LEADER_ROLES, ROLES } from "utils/helpers/enums/roles.enum";
import AccountAction from "components/popups/account-action";
import { confirmationPopup } from "utils/helpers/common/alert-service";
import useAuth from "containers/auth/useHooks";
import { UpgradeProfileDTO } from "utils/helpers/models/auth/upgrade-profile.dto";
import { useNavigate } from "react-router-dom";
import { PROFILE_TYPES } from "utils/helpers/enums/shared.enum";
import useAttendee from "../useHooks";

type FormValues = {
  oldPassword: string;
  password: string;
  confirmPassword: string;
};
const AttendeeGeneralSettings: React.FC = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();
  const [activeSection, setActiveSection] = useState("Account Preference");
  const navigate = useNavigate();
  const {
    getSwitchRoles,
    getDefaultRole,
    getCurrentRole,
    getUpgradeRouteByRole,
    isProfileCompleted,
  } = useStore();

  // Check profile completion on mount - redirect to home if not completed
  useEffect(() => {
    const profileCompleted = isProfileCompleted();
    if (!profileCompleted) {
      navigate(siteRoutes.attendeeHome, { replace: true });
    }
  }, [isProfileCompleted, navigate]);

  const roles = getSwitchRoles();
  const defaultRole = getDefaultRole();
  const currentRole = getCurrentRole();
  const { switchProfile, upgradeProfile, getPrivacyPolicy } = useAuth();
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [openActionDialog, setOpenActionDialog] = useState(false);
  const [roleForActionDialog, setRoleForActionDialog] = useState<ROLES | null>(
    null
  );
  const [termConditionContent, setTermConditionContent] = useState<[]>([]);
  const [privacyPolicyContent, setPrivacyPolicyContent] = useState<[]>([]);
  const [attendeeProfileData, setAttendeeProfileData] = useState<any>(null);
  const [enableNotifications, setEnableNotifications] = useState(
    Boolean(attendeeProfileData?.notificationEnabled)
  );

  const breadcrumbLinks: AttendeBreadcrumbLink[] = [
    { title: "Home ", path: siteRoutes.home },
    { title: "General Settings", path: siteRoutes.generalSettingsAttendee },
  ];

  const onSubmit = (data: FormValues) => {
    if (data.password !== data.confirmPassword) {
      alert("Passwords do not match!");
    } else {
      console.log("Password reset successfully:", data);
    }
  };

  const sidebarLinks = [
    { id: 1, label: "Account Preference", icon: <AiOutlineSwap /> },
    { id: 2, label: "Notifications", icon: <FiBell /> },
    // { id: 3, label: "Change password", icon: <FiKey /> },
    { id: 4, label: "Privacy Policy", icon: <FiFileText /> },
    { id: 5, label: "Term & Condition", icon: <FiList /> },
  ];

  const { getAttendeeProfile, toggleNotification } = useAttendee();

  const getData = async () => {
    await getAttendeeProfile((profile: any) => {
      setAttendeeProfileData(profile);
      setEnableNotifications(profile?.notificationEnabled);
    });
  };

  useEffect(() => {
    getData();
  }, []);

  const handleToggleNotifications = async () => {
    setEnableNotifications((prev) => !prev);
    await toggleNotification();
  };
  const getRolesToUpgrade = () => {
    return Object.values(LEADER_ROLES).filter((role) => !roles.includes(role));
  };

  const handleOpenActionDialog = (role: ROLES) => {
    setOpenActionDialog(true);
    setRoleForActionDialog(role);
  };

  const handleSwitchProfile = async (role: ROLES) => {
    if (role !== currentRole) {
      const result = await confirmationPopup(
        "Are you sure you want to switch profile?"
      );
      if (result.isConfirmed) {
        switchProfile({ role });
      }
    }
  };

  const handleUpgradeProfile = async (role: string) => {
    const pathToNavigate = getUpgradeRouteByRole(role as ROLES);
    if (pathToNavigate) {
      navigate(`${pathToNavigate}?upgrade=true`);
    } else {
      const result = await confirmationPopup(
        `Are you sure you want to upgrade to ${role} profile?`
      );
      if (result.isConfirmed) {
        const body = new UpgradeProfileDTO({
          role: role as ROLES,
          profileType: PROFILE_TYPES.LEADER,
        });
        upgradeProfile(body);
      }
    }
  };

  useEffect(() => {
    getPrivacyPolicy(
      { heading: "Parent/Legal Guardian Attestation" },
      setTermConditionContent
    );
    getPrivacyPolicy({ heading: "Privacy Policy" }, setPrivacyPolicyContent);
  }, []);

  return (
    <div className="container">
      <AttendeeBreadcrumb links={breadcrumbLinks} />
      <h2 className="text-3xl text-blackie font-medium mb-4">
        General Settings
      </h2>

      <div className="flex flex-wrap sm:min-h-screen">
        {/* Sidebar */}

        <div className="w-full sm:w-[35%] md:w-1/4 bg-white">
          <ul>
            {sidebarLinks.map((link) => (
              <li
                key={link.id}
                onClick={() => {
                  if (link.label !== "Notifications") {
                    setActiveSection(link.label);
                  }
                }}
                className={`flex items-center justify-between px-3 py-4 cursor-pointer rounded-lg bordr-linear ${
                  activeSection === link.label && link.label !== "Notifications"
                    ? "text-AttendeeBlue"
                    : ""
                }`}
              >
                {/* Left Section: Icon and Label */}
                <div className="flex items-center">
                  <span
                    className={`mr-3 text-xl ${
                      activeSection === link.label &&
                      link.label !== "Notifications"
                        ? "text-AttendeeBlue"
                        : "text-primaryBlueText"
                    }`}
                  >
                    {link.icon}
                  </span>
                  <span
                    className={`${
                      activeSection === link.label &&
                      link.label !== "Notifications"
                        ? "text-AttendeeBlue"
                        : "text-primaryBlueText"
                    }`}
                  >
                    {link.label}
                  </span>
                </div>

                {/* Right Section: Arrow Icon or Notification Toggle */}
                {link.label === "Notifications" ? (
                  <button
                   onClick={(e) => {
                      e.stopPropagation();
                      handleToggleNotifications();
                    }}
                    className={`w-10 h-6 flex items-center rounded-full p-1 ${
                      enableNotifications ? "bg-attendeeBlue" : "bg-gray-300"
                    }`}
                  >
                    <div
                      className={`h-4 w-4 bg-white rounded-full transform duration-300 ${
                        enableNotifications ? "translate-x-4" : ""
                      }`}
                    ></div>
                  </button>
                ) : (
                  <MdArrowForwardIos
                    className={`text-lg ${
                      activeSection === link.label
                        ? "text-AttendeeBlue"
                        : "text-gray-500"
                    }`}
                  />
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Main Content */}
        <div className="flex-1 mt-5 sm:mt-0 sm:ml-6 ">
          {activeSection === "Account Preference" && (
            <div className="w-full md:w-2/3">
              <h2 className="text-2xl text-blackie font-semibold mb-4">
                Switch Mode
              </h2>

              {roles.map((role, index) => {
                return (
                  <div
                    className="flex items-center cursor-pointer justify-between p-4 bg-gray-100 rounded-lg mt-4"
                    key={index}
                    onClick={() => handleSwitchProfile(role as ROLES)}
                  >
                    <div className="flex gap-3 items-center cursor-pointer">
                      {currentRole === role ? (
                        <p className="font-medium text-blackie capitalize">
                          {role.toLowerCase()} Account
                        </p>
                      ) : (
                        <p className="font-medium text-blackie capitalize">
                          Switch to {role.toLowerCase()}
                        </p>
                      )}
                      {role === defaultRole && (
                        <p className="px-2 py-1 text-sm bg-fade text-primaryBlueText rounded-full">
                          Active
                        </p>
                      )}
                    </div>
                    {defaultRole !== role && (
                      <button
                        className="text-blackie"
                        // onClick={() => {
                        // handleOpenActionDialog(role as ROLES);
                        // }}
                      >
                        ⋮
                      </button>
                    )}
                  </div>
                );
              })}
              {getRolesToUpgrade().map((role, index) => {
                return (
                  <div
                    className="flex cursor-pointer items-center justify-between p-4 bg-gray-100 rounded-lg mt-4"
                    key={index}
                    onClick={() => handleUpgradeProfile(role as string)}
                  >
                    <div className="flex gap-3 items-center">
                      <p className="font-medium text-blackie capitalize">
                        Upgrade to {role.toLowerCase()}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {activeSection === "Change password" && (
            <div className="w-full md:w-2/3  flex flex-col">
              {/* Form Container */}
              <div className="flex-1 flex items-center justify-center">
                <div className="w-full">
                  <div className="w-full mb-6">
                    <h1 className="text-2xl text-blackie font-semibold">
                      Change Password
                    </h1>
                    <p className="text-sm text-labelTextColor mt-2">
                      Please enter old password and confirm to continue.
                    </p>
                  </div>
                  <form onSubmit={handleSubmit(onSubmit)} className="mt-6">
                    {/* Old Password Input */}
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center justify-between">
                        <label className="text-labelTextColor text-lg">
                          Old Password
                        </label>
                        <div
                          className="cursor-pointer flex items-center text-label"
                          onClick={() => setShowOldPassword(!showOldPassword)}
                        >
                          {showOldPassword ? (
                            <>
                              <AiFillEyeInvisible size={18} className="mr-1" />
                              <span>Hide</span>
                            </>
                          ) : (
                            <>
                              <AiFillEye size={18} className="mr-1" />
                              <span>Show</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="w-full h-[51px] flex items-center px-3 rounded-lg border border-borderColor">
                        <input
                          type={showOldPassword ? "text" : "password"}
                          placeholder="Enter your old password"
                          {...register("oldPassword", {
                            required: "Old Password is required",
                          })}
                          className="w-full h-full px-3 outline-none"
                        />
                      </div>
                      {errors.oldPassword && (
                        <span className="text-red-500 text-sm">
                          {errors.oldPassword.message}
                        </span>
                      )}
                    </div>

                    {/* New Password Input */}
                    <div className="flex flex-col gap-2 mt-4">
                      <div className="flex items-center justify-between">
                        <label className="text-labelTextColor text-lg">
                          New Password
                        </label>
                        <div
                          className="cursor-pointer flex items-center text-label"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <>
                              <AiFillEyeInvisible size={18} className="mr-1" />
                              <span>Hide</span>
                            </>
                          ) : (
                            <>
                              <AiFillEye size={18} className="mr-1" />
                              <span>Show</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="w-full h-[51px] flex items-center px-3 rounded-lg border border-borderColor">
                        <input
                          type={showPassword ? "text" : "password"}
                          placeholder="Enter your new password"
                          {...register("password", {
                            required: "New Password is required",
                            minLength: {
                              value: 8,
                              message:
                                "Password must be at least 8 characters long",
                            },
                          })}
                          className="w-full h-full px-3 outline-none"
                        />
                      </div>
                      {errors.password && (
                        <span className="text-red-500 text-sm">
                          {errors.password.message}
                        </span>
                      )}
                    </div>

                    {/* Confirm Password Input */}
                    <div className="flex flex-col gap-2 mt-4">
                      <div className="flex items-center justify-between">
                        <label className="text-labelTextColor text-lg">
                          Confirm Password
                        </label>
                        <div
                          className="cursor-pointer flex items-center text-[#606060]"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                        >
                          {showConfirmPassword ? (
                            <>
                              <AiFillEyeInvisible size={18} className="mr-1" />
                              <span>Hide</span>
                            </>
                          ) : (
                            <>
                              <AiFillEye size={18} className="mr-1" />
                              <span>Show</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="w-full h-[51px] flex items-center px-3 rounded-lg border border-borderColor">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="Confirm your new password"
                          {...register("confirmPassword", {
                            required: "Confirm Password is required",
                          })}
                          className="w-full h-full px-3 outline-none"
                        />
                      </div>
                      {errors.confirmPassword && (
                        <span className="text-red-500 text-sm">
                          {errors.confirmPassword.message}
                        </span>
                      )}
                    </div>

                    {/* Submit Button */}
                    <div className="flex justify-start flex-col gap-2 pt-4">
                      <button
                        type="submit"
                        className="h-[64px]  text-xl bg-primaryBlueBg rounded-full text-white w-full sm:w-1/2"
                      >
                        Reset Password
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}
          {activeSection === "Privacy Policy" && (
            <div>
              {privacyPolicyContent?.map((item: any) => (
                <div key={item.id} className="space-y-4">
                  <h2 className="text-2xl font-bold text-black mb-4">
                    {item.heading}
                  </h2>
                  <div
                    className="text-labelTextColor "
                    dangerouslySetInnerHTML={{ __html: item.content }}
                  />
                </div>
              ))}
            </div>
          )}
          {activeSection === "Term & Condition" && (
            <div>
              {termConditionContent?.map((item: any) => (
                <div key={item.id} className="space-y-4">
                  <h2 className="text-2xl font-bold text-black mb-4">
                    {item.heading}
                  </h2>
                  <div
                    className="text-labelTextColor "
                    dangerouslySetInnerHTML={{ __html: item.content }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {openActionDialog && (
        <AccountAction
          isOpen={openActionDialog}
          onClose={() => setOpenActionDialog(false)}
          role={roleForActionDialog}
        />
      )}
    </div>
  );
};

export default AttendeeGeneralSettings;
